import React, { FC, Fragment, useEffect, useState } from 'react';
import { Grid, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { ParticipantView, QuestionView, ResponseView } from '@views';
import { useNavigationEligibility } from '@components/SurveyDrawer/NavigationEligibilityContextProvider';

interface AccountabilityPartnerResponseProps {
  currentQuestion: QuestionView;
  activeParticipants: ParticipantView[];
}

/** "No Preference" — assign me someone. The pairing job reads -1 as exactly that. */
const NO_PREFERENCE = -1;

export const AccountabilityPartnerResponse: FC<AccountabilityPartnerResponseProps> = ({
  currentQuestion,
  activeParticipants,
}) => {
  const { triggerEligibility } = useNavigationEligibility();
  const [selected, setSelected] = useState<number>(NO_PREFERENCE);

  useEffect(() => {
    const partner = currentQuestion.responses.find((r) => r.responseType === 'AccountabilityPartner');
    if (!partner) return;

    // Naming nobody is a real answer to this question — it offers to assign a partner for you — so
    // record it up front instead of letting the question submit blank. A blank is not equivalent:
    // the pairing job reads the answer as Number(responseText), so '' becomes 0, which marks the
    // user as having responded but *not* as "no preference", and one-way picks naming them are
    // then discarded. It is also what keeps Finish reachable without touching the dropdown.
    if (!partner.textResponse) {
      partner.selected = true;
      partner.textResponse = String(NO_PREFERENCE);
    }
    setSelected(Number(partner.textResponse));
    triggerEligibility();
  }, [currentQuestion]);

  const handleAccountabilityPartnerResponse = (e: SelectChangeEvent<number>, opt: ResponseView) => {
    const optIdx = currentQuestion.responses.findIndex((x) => x.id === opt.id);
    currentQuestion.responses.forEach((x) => {
      x.selected = false;
      x.textResponse = '';
    });
    currentQuestion.responses[optIdx].selected = true;
    currentQuestion.responses[optIdx].textResponse = String(e.target.value);
    setSelected(Number(e.target.value));
    triggerEligibility();
  };

  const currentStepResponses = currentQuestion.responses;
  return (
    <Fragment>
      {currentStepResponses.some((opt) => opt.responseType === 'AccountabilityPartner') &&
        currentQuestion.responses.map((opt) => {
          if (opt.responseType === 'AccountabilityPartner') {
            return (
              <Grid
                container
                key={opt.id}
                size={{ xs: 12 }}
                spacing={2}
                sx={{
                  flexWrap: 'wrap',
                  alignContent: 'center',
                  justifyContent: 'center',
                  display: 'flex',
                  gap: 2,
                  paddingTop: 2,
                  paddingBottom: 2,
                }}
              >
                <Select value={selected} onChange={(e) => handleAccountabilityPartnerResponse(e, opt)}>
                  <MenuItem key={'default'} value={NO_PREFERENCE}>
                    No Preference
                  </MenuItem>
                  {activeParticipants.map((participant) => {
                    return (
                      <MenuItem value={participant.id} key={`participant-${participant.id}`}>
                        {participant.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Grid>
            );
          }
          return null;
        })}
    </Fragment>
  );
};
